/**
 * What the import wizard's preview shows, derived from the prepared manufactures and the reads
 * the screen makes. Pure: the screen (ManufactureImportScreen.js) only fetches and renders.
 *
 *  - `existingRecordsRange`: the window the screen reads existing manufactures and revisions over;
 *  - `latestRevisionDate` / `lastHandEnteredManufactureDate`: the two dates the preview shows per
 *    store, from either read shape (Firestore docs or the Supabase-mode API items);
 *  - `buildPreviewRows` / `selectImportItems`: one view-model per prepared manufacture, and the
 *    items the run writes once the duplicate switch has been applied.
 */

import { type ImportDate } from '../importCells'
import { type DateLike, asDate } from '../timezoneUtils'
import { type ManufactureInfo } from './buildInfo'
import { type DuplicateResult } from './duplicates'
import { type ManufactureDraft, type RowRef } from './types'

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Days added on both sides of the drafts' span. A prepared manufacture sits at midday
 * store-local, while an existing record on the same STORE business day can be up to a day away
 * from that instant (time zone + autoclose shift); two days keeps every such record in the read.
 * Records outside the span are harmless: duplicate matching compares business days.
 */
export const EXISTING_RANGE_MARGIN_DAYS = 2

/** `{year, month, day, …}` → `10.07.2026`, with the time only when the file gave one. */
export const formatImportDate = (date: ImportDate): string => {
  const pad = (n: number): string => String(n).padStart(2, '0')
  const day = `${pad(date.day)}.${pad(date.month)}.${date.year}`
  return date.hasTime ? `${day} ${pad(date.hour)}:${pad(date.minute)}` : day
}

/**
 * The window to read existing manufactures and revisions over: the drafts' span widened by the
 * margin, and always reaching `now` — a revision done today after a manufacture of last month
 * is exactly what the revision warning exists for, and the last hand-entered manufacture is
 * looked up over the same window. `null` when there are no dates (nothing to preview).
 */
export const existingRecordsRange = (
  dates: Date[],
  now: Date
): { startDate: Date; endDate: Date } | null => {
  const times = dates.map((date) => date.getTime()).filter((time) => Number.isFinite(time))
  if (times.length === 0) return null
  const margin = EXISTING_RANGE_MARGIN_DAYS * DAY_MS
  return {
    startDate: new Date(Math.min(...times) - margin),
    endDate: new Date(Math.max(now.getTime(), Math.max(...times) + margin)),
  }
}

const recordField = (record: unknown, field: string): unknown =>
  record !== null && typeof record === 'object' ? (record as Record<string, unknown>)[field] : undefined

const latest = (dates: Array<Date | null>): Date | null =>
  dates.reduce<Date | null>(
    (best, date) => (date !== null && (best === null || date.getTime() > best.getTime()) ? date : best),
    null
  )

/**
 * The newest revision of `getRevisionsOnDateRange(…, flat = true, store)`.
 *  - Firestore: `date` is a Timestamp (revisions are hard-deleted, so every doc counts).
 *  - Supabase: the backend returns the Firestore-era alias `date` (converted by the typed client),
 *    with `revisionDate` as the column's own name; a soft-deleted row (`deletedAt`) is not a
 *    revision anymore and is skipped.
 */
export const latestRevisionDate = (revisions: unknown[]): Date | null =>
  latest(
    revisions.map((revision) => {
      if (recordField(revision, 'deletedAt')) return null
      return (
        asDate(recordField(revision, 'date') as DateLike) ??
        asDate(recordField(revision, 'revisionDate') as DateLike)
      )
    })
  )

/**
 * The newest manufacture in `existing` that was NOT created by an import (`manufactureSource`
 * absent/null — every hand-entered manufacture, including the whole pre-import history). Read
 * shapes: Firestore doc field `manufactureSource`; Supabase `manufactureSource` (camelCase
 * transform) or the raw `manufacture_source`.
 */
export const lastHandEnteredManufactureDate = (existing: unknown[]): Date | null =>
  latest(
    existing.map((manufacture) => {
      const source =
        recordField(manufacture, 'manufactureSource') ?? recordField(manufacture, 'manufacture_source')
      if (source === 'import') return null
      return asDate(recordField(manufacture, 'date') as DateLike)
    })
  )

/** One prepared manufacture: the draft it came from and the writer payload built from it. */
export interface PreparedManufacture {
  key: string
  draft: ManufactureDraft
  info: ManufactureInfo
}

export interface ManufacturePreviewRow {
  key: string
  fileName: string
  refs: RowRef[]
  productName: string
  /** The date as written in the file (`formatImportDate`). */
  date: string
  quantity: number
  batchNumber: string | null
  ingredientCount: number
  totalSum: number
  /** Matches a manufacture already in the books (skipped unless duplicates are included). */
  duplicate: boolean
  /** Identical to another manufacture of this run (both kept). */
  twin: boolean
  /** Dated before the store's last stock revision (warning only). */
  beforeRevision: boolean
  /** The run will write it. */
  willImport: boolean
}

export interface BuildPreviewRowsOptions {
  prepared: PreparedManufacture[]
  duplicates: DuplicateResult
  beforeRevision: string[]
  includeDuplicates: boolean
}

const productName = (product: unknown): string => {
  const name = recordField(product, 'name')
  return typeof name === 'string' ? name : ''
}

export const buildPreviewRows = ({
  prepared,
  duplicates,
  beforeRevision,
  includeDuplicates,
}: BuildPreviewRowsOptions): ManufacturePreviewRow[] => {
  const beforeRevisionKeys = new Set(beforeRevision)
  return prepared.map(({ key, draft, info }) => {
    const duplicate = duplicates.existingMatch.has(key)
    return {
      key,
      fileName: draft.fileName,
      refs: draft.refs,
      productName: productName(info.product),
      date: formatImportDate(draft.date),
      quantity: info.quantity,
      batchNumber: info.batchNumber ?? null,
      ingredientCount: info.ingredients.length,
      totalSum: info.totalSum,
      duplicate,
      twin: duplicates.inRunTwins.has(key),
      beforeRevision: beforeRevisionKeys.has(key),
      willImport: includeDuplicates || !duplicate,
    }
  })
}

/** The items `runManufactureImport` receives: every prepared manufacture the switch keeps. */
export const selectImportItems = (
  prepared: PreparedManufacture[],
  duplicates: DuplicateResult,
  includeDuplicates: boolean
): Array<{ key: string; info: ManufactureInfo; refs: RowRef[] }> =>
  prepared
    .filter(({ key }) => includeDuplicates || !duplicates.existingMatch.has(key))
    .map(({ key, draft, info }) => ({ key, info, refs: draft.refs }))

export interface PreviewCounts {
  prepared: number
  toImport: number
  /** Probable duplicates the run will skip (0 when duplicates are included). */
  skippedDuplicates: number
  duplicates: number
  twins: number
  beforeRevision: number
  /** Σ totalSum of what will be written. */
  totalSum: number
}

export const previewCounts = (rows: ManufacturePreviewRow[]): PreviewCounts => ({
  prepared: rows.length,
  toImport: rows.filter((row) => row.willImport).length,
  skippedDuplicates: rows.filter((row) => !row.willImport).length,
  duplicates: rows.filter((row) => row.duplicate).length,
  twins: rows.filter((row) => row.twin).length,
  beforeRevision: rows.filter((row) => row.beforeRevision).length,
  totalSum: rows.filter((row) => row.willImport).reduce((sum, row) => sum + row.totalSum, 0),
})
